import { useEffect, useMemo } from 'react';
import type { ChangeEvent } from 'react';
import type { IntakeShellRenderProps } from './IntakeShell.js';

/**
 * wd-2.3.5 — Step 4 "Payments & accounting integrations" for the intake portal.
 *
 * Fields mirror `intakeStep4Schema` in `packages/config/src/intake-steps.ts`:
 *
 *   quickBooks: discriminatedUnion on `variant`
 *     - 'qbo'  → companyId?, authorizedAt?, syncScopes[], accountantEmail?
 *     - 'qbd'  → accountantEmail, accountantName, exportCadence, _comment?
 *     - 'none' → confirmedNoAccountingSync: true
 *   ach: { desired, bankName?, contactName?, contactEmail?, contactPhone?, _note? }
 *     (refine: if desired, bankName + contactName + (email or phone) required)
 *   paymentChannels: { online (default true), inPerson (default false),
 *                      terminalModel?, manualPhone (default false) }
 *
 * Intuit OAuth is deferred to a later ticket — for now the QBO variant lets
 * the prospect declare intent and optionally stamp `authorizedAt` manually
 * from an external authorization step. The schema is forgiving here
 * (all QBO fields are optional) so intent alone is a valid submission.
 */

type QbVariant = 'qbo' | 'qbd' | 'none';

const EXPORT_CADENCE_OPTIONS = ['weekly', 'biweekly', 'monthly'] as const;
type ExportCadence = (typeof EXPORT_CADENCE_OPTIONS)[number];

const QBO_SYNC_SCOPES = [
  'invoices',
  'payments',
  'bank_deposits',
  'products',
] as const;
type QboSyncScope = (typeof QBO_SYNC_SCOPES)[number];

const TERMINAL_MODELS = [
  'bbpos_wisepos_e',
  'stripe_reader_s700',
  'tbd',
] as const;
type TerminalModel = (typeof TERMINAL_MODELS)[number];

type QuickBooksDraft = {
  variant: QbVariant | '';
  // qbo
  companyId: string;
  authorizedAt: string;
  syncScopes: QboSyncScope[];
  accountantEmailQbo: string;
  // qbd
  accountantEmail: string;
  accountantName: string;
  exportCadence: ExportCadence | '';
  comment: string;
  // none
  confirmedNoAccountingSync: boolean;
};

type AchDraft = {
  desired: boolean;
  bankName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  note: string;
};

type ChannelsDraft = {
  online: boolean;
  inPerson: boolean;
  terminalModel: TerminalModel | '';
  manualPhone: boolean;
};

type Step4Draft = {
  quickBooks: QuickBooksDraft;
  ach: AchDraft;
  paymentChannels: ChannelsDraft;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringField(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function boolField(value: unknown): boolean {
  return value === true;
}

function readVariant(value: unknown): QbVariant | '' {
  return value === 'qbo' || value === 'qbd' || value === 'none' ? value : '';
}

function readCadence(value: unknown): ExportCadence | '' {
  return value === 'weekly' || value === 'biweekly' || value === 'monthly'
    ? value
    : '';
}

function readTerminal(value: unknown): TerminalModel | '' {
  return value === 'bbpos_wisepos_e'
    || value === 'stripe_reader_s700'
    || value === 'tbd'
    ? value
    : '';
}

function readSyncScopes(value: unknown): QboSyncScope[] {
  if (!Array.isArray(value)) return [];
  const set = new Set<QboSyncScope>();
  for (const v of value) {
    if (
      v === 'invoices'
      || v === 'payments'
      || v === 'bank_deposits'
      || v === 'products'
    ) {
      set.add(v);
    }
  }
  return QBO_SYNC_SCOPES.filter((s) => set.has(s));
}

function readQuickBooks(value: unknown): QuickBooksDraft {
  const qb = isRecord(value) ? value : {};
  return {
    variant: readVariant(qb.variant),
    companyId: stringField(qb.companyId),
    authorizedAt: stringField(qb.authorizedAt),
    syncScopes: readSyncScopes(qb.syncScopes),
    accountantEmailQbo:
      qb.variant === 'qbo' ? stringField(qb.accountantEmail) : '',
    accountantEmail:
      qb.variant === 'qbd' ? stringField(qb.accountantEmail) : '',
    accountantName: stringField(qb.accountantName),
    exportCadence: readCadence(qb.exportCadence),
    comment: stringField(qb._comment),
    confirmedNoAccountingSync: boolField(qb.confirmedNoAccountingSync),
  };
}

function readAch(value: unknown): AchDraft {
  const ach = isRecord(value) ? value : {};
  return {
    desired: boolField(ach.desired),
    bankName: stringField(ach.bankName),
    contactName: stringField(ach.contactName),
    contactEmail: stringField(ach.contactEmail),
    contactPhone: stringField(ach.contactPhone),
    note: stringField(ach._note),
  };
}

function readChannels(value: unknown): ChannelsDraft {
  const ch = isRecord(value) ? value : {};
  return {
    online: 'online' in ch ? boolField(ch.online) : true,
    inPerson: boolField(ch.inPerson),
    terminalModel: readTerminal(ch.terminalModel),
    manualPhone: boolField(ch.manualPhone),
  };
}

function readStep4(draft: Record<string, unknown>): Step4Draft {
  return {
    quickBooks: readQuickBooks(draft.quickBooks),
    ach: readAch(draft.ach),
    paymentChannels: readChannels(draft.paymentChannels),
  };
}

function groupIssuesByPath(
  issues: ReadonlyArray<{ path: string; message: string }>,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const issue of issues) {
    const key = issue.path === '' ? '_root' : issue.path;
    const list = out[key] ?? [];
    list.push(issue.message);
    out[key] = list;
  }
  return out;
}

function fieldErrorId(field: string): string {
  return `intake-step4-${field}-error`.replace(/[^a-z0-9-]/gi, '-');
}

// US phone: exactly the same shape the schema's `phoneUsRegex` accepts.
// Used purely for client-side nudge; server is the source of truth.
const PHONE_REGEX = /^\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;

function isBlankPhone(value: string): boolean {
  return value.trim() === '';
}

function describePhoneError(value: string): string | null {
  if (isBlankPhone(value)) return null;
  return PHONE_REGEX.test(value) ? null : 'Enter a US phone number.';
}

function describeEmailError(value: string): string | null {
  if (value.trim() === '') return null;
  // Minimal client-side nudge — Zod's email regex is the source of truth.
  return /.+@.+\..+/.test(value) ? null : 'Enter a valid email.';
}

/**
 * Build the quickBooks sub-payload. Variant-specific keys are only emitted
 * for the selected variant so we don't dirty the draft with leftover
 * fields from a prior variant selection.
 */
function serializeQuickBooks(
  qb: QuickBooksDraft,
): Record<string, unknown> | undefined {
  if (qb.variant === '') return undefined;
  if (qb.variant === 'qbo') {
    const out: Record<string, unknown> = {
      variant: 'qbo',
      syncScopes: qb.syncScopes,
    };
    if (qb.companyId.trim() !== '') out.companyId = qb.companyId.trim();
    if (qb.authorizedAt.trim() !== '') out.authorizedAt = qb.authorizedAt;
    if (qb.accountantEmailQbo.trim() !== '') {
      out.accountantEmail = qb.accountantEmailQbo.trim();
    }
    return out;
  }
  if (qb.variant === 'qbd') {
    const out: Record<string, unknown> = {
      variant: 'qbd',
      accountantEmail: qb.accountantEmail.trim(),
      accountantName: qb.accountantName.trim(),
    };
    if (qb.exportCadence !== '') out.exportCadence = qb.exportCadence;
    if (qb.comment.trim() !== '') out._comment = qb.comment.trim();
    return out;
  }
  // 'none'
  return {
    variant: 'none',
    confirmedNoAccountingSync: qb.confirmedNoAccountingSync,
  };
}

function serializeAch(ach: AchDraft): Record<string, unknown> {
  const out: Record<string, unknown> = { desired: ach.desired };
  if (ach.bankName.trim() !== '') out.bankName = ach.bankName.trim();
  if (ach.contactName.trim() !== '') out.contactName = ach.contactName.trim();
  if (ach.contactEmail.trim() !== '') {
    out.contactEmail = ach.contactEmail.trim();
  }
  if (ach.contactPhone.trim() !== '') {
    out.contactPhone = ach.contactPhone.trim();
  }
  if (ach.note.trim() !== '') out._note = ach.note.trim();
  return out;
}

function serializeChannels(ch: ChannelsDraft): Record<string, unknown> {
  const out: Record<string, unknown> = {
    online: ch.online,
    inPerson: ch.inPerson,
    manualPhone: ch.manualPhone,
  };
  if (ch.inPerson && ch.terminalModel !== '') {
    out.terminalModel = ch.terminalModel;
  }
  return out;
}

export type Step4PaymentsProps = IntakeShellRenderProps;

export function Step4Payments({
  stepDraft,
  onChange,
  issues,
}: Step4PaymentsProps): JSX.Element {
  const errorsByPath = useMemo(() => groupIssuesByPath(issues), [issues]);
  const value = useMemo(() => readStep4(stepDraft), [stepDraft]);
  const { quickBooks: qb, ach, paymentChannels: ch } = value;

  const serverErrorFor = (path: string): string | null => {
    const list = errorsByPath[path];
    return list && list.length > 0 ? list.join(' · ') : null;
  };

  // Seed channel defaults on first mount so the draft round-trips cleanly.
  // Matches the Step 2 pattern for colors.background / colors.text.
  useEffect(() => {
    if (!isRecord(stepDraft.paymentChannels)) {
      onChange({
        paymentChannels: {
          online: true,
          inPerson: false,
          manualPhone: false,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // QuickBooks handlers
  const patchQb = (patch: Partial<QuickBooksDraft>): void => {
    const next: QuickBooksDraft = { ...qb, ...patch };
    const serialized = serializeQuickBooks(next);
    onChange({ quickBooks: serialized });
  };

  const onVariantChange = (variant: QbVariant): void => {
    // Switching variants resets variant-specific fields. Server schema
    // would reject stale keys via the discriminated union otherwise.
    if (variant === qb.variant) return;
    const next: QuickBooksDraft = {
      ...qb,
      variant,
      // clear qbo fields
      companyId: '',
      authorizedAt: '',
      syncScopes: [],
      accountantEmailQbo: '',
      // clear qbd fields
      accountantEmail: '',
      accountantName: '',
      exportCadence: '',
      comment: '',
      // clear none
      confirmedNoAccountingSync: false,
    };
    onChange({ quickBooks: serializeQuickBooks(next) });
  };

  const onSyncScopeToggle = (scope: QboSyncScope): void => {
    const has = qb.syncScopes.includes(scope);
    const nextScopes = has
      ? qb.syncScopes.filter((s) => s !== scope)
      : QBO_SYNC_SCOPES.filter((s) => s === scope || qb.syncScopes.includes(s));
    patchQb({ syncScopes: nextScopes });
  };

  // ACH handlers
  const patchAch = (patch: Partial<AchDraft>): void => {
    const next: AchDraft = { ...ach, ...patch };
    onChange({ ach: serializeAch(next) });
  };

  // Channels handlers
  const patchChannels = (patch: Partial<ChannelsDraft>): void => {
    const next: ChannelsDraft = { ...ch, ...patch };
    onChange({ paymentChannels: serializeChannels(next) });
  };

  // Client-side conditional validation for ACH.
  const achBankError = serverErrorFor('ach.bankName');
  const achContactNameError = serverErrorFor('ach.contactName');
  const achEmailServerError = serverErrorFor('ach.contactEmail');
  const achPhoneServerError = serverErrorFor('ach.contactPhone');
  const achRootServerError = serverErrorFor('ach');

  const achDesiredGateMessage =
    ach.desired
    && (ach.bankName.trim() === ''
      || ach.contactName.trim() === ''
      || (ach.contactEmail.trim() === '' && ach.contactPhone.trim() === ''))
      ? 'Bank name, contact name, and either an email or a phone are required for ACH.'
      : null;

  const achEmailClientError = describeEmailError(ach.contactEmail);
  const achPhoneClientError = describePhoneError(ach.contactPhone);

  const qbRootServerError = serverErrorFor('quickBooks');
  const qbVariantServerError = serverErrorFor('quickBooks.variant');
  const qbAccountantEmailQbdServerError = serverErrorFor(
    'quickBooks.accountantEmail',
  );
  const qbAccountantNameServerError = serverErrorFor(
    'quickBooks.accountantName',
  );
  const qbExportCadenceServerError = serverErrorFor(
    'quickBooks.exportCadence',
  );
  const qbConfirmNoneServerError = serverErrorFor(
    'quickBooks.confirmedNoAccountingSync',
  );
  const channelsRootServerError = serverErrorFor('paymentChannels');
  const channelsTerminalServerError = serverErrorFor(
    'paymentChannels.terminalModel',
  );

  return (
    <form
      className="ferro-form ferro-intake__form"
      aria-label="Step 4 — Payments & accounting"
      noValidate
      onSubmit={(e) => e.preventDefault()}
    >
      <p className="ferro-muted">
        Tell us how you want to handle accounting sync, ACH transfers, and
        in-person payments. These control the payment surface we expose to
        your staff and customers on day one.
      </p>

      {/* ── QuickBooks ─────────────────────────────────────── */}
      <section
        className="ferro-field"
        aria-labelledby="intake-step4-qb-heading"
      >
        <h2 id="intake-step4-qb-heading" className="ferro-field__label">
          QuickBooks accounting
        </h2>
        <p className="ferro-muted">
          QuickBooks Online connects through Intuit OAuth after launch.
          QuickBooks Desktop exports to your accountant on a regular cadence.
        </p>
        <div
          role="radiogroup"
          aria-labelledby="intake-step4-qb-heading"
          className="ferro-field ferro-intake__radioGroup"
          data-testid="intake-step4-qb-variant-group"
        >
          <label className="ferro-field ferro-intake__radio">
            <input
              type="radio"
              name="qb-variant"
              value="qbo"
              checked={qb.variant === 'qbo'}
              onChange={() => onVariantChange('qbo')}
              data-testid="intake-step4-qb-variant-qbo"
            />
            <span>QuickBooks Online (connect later via Intuit)</span>
          </label>
          <label className="ferro-field ferro-intake__radio">
            <input
              type="radio"
              name="qb-variant"
              value="qbd"
              checked={qb.variant === 'qbd'}
              onChange={() => onVariantChange('qbd')}
              data-testid="intake-step4-qb-variant-qbd"
            />
            <span>QuickBooks Desktop (email exports to accountant)</span>
          </label>
          <label className="ferro-field ferro-intake__radio">
            <input
              type="radio"
              name="qb-variant"
              value="none"
              checked={qb.variant === 'none'}
              onChange={() => onVariantChange('none')}
              data-testid="intake-step4-qb-variant-none"
            />
            <span>No accounting sync</span>
          </label>
        </div>
        {qbVariantServerError ? (
          <span
            id={fieldErrorId('qb-variant')}
            role="alert"
            className="ferro-form__error"
          >
            {qbVariantServerError}
          </span>
        ) : qbRootServerError ? (
          <span
            id={fieldErrorId('qb-root')}
            role="alert"
            className="ferro-form__error"
            data-testid="intake-step4-qb-root-servererror"
          >
            {qbRootServerError}
          </span>
        ) : null}

        {/* QBO variant fields */}
        {qb.variant === 'qbo' ? (
          <div
            className="ferro-intake__conditional"
            data-testid="intake-step4-qbo-block"
          >
            <label className="ferro-field">
              <span className="ferro-field__label">
                Intuit Company ID (optional)
              </span>
              <input
                type="text"
                className="ferro-field__input"
                value={qb.companyId}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  patchQb({ companyId: e.target.value })
                }
                placeholder="e.g. 9123456789"
                data-testid="intake-step4-qbo-company-id"
              />
            </label>
            <fieldset
              className="ferro-field"
              data-testid="intake-step4-qbo-scopes"
            >
              <legend className="ferro-field__label">
                Sync scopes (optional)
              </legend>
              {QBO_SYNC_SCOPES.map((scope) => (
                <label
                  key={scope}
                  className="ferro-field ferro-intake__checkbox"
                >
                  <input
                    type="checkbox"
                    checked={qb.syncScopes.includes(scope)}
                    onChange={() => onSyncScopeToggle(scope)}
                    data-testid={`intake-step4-qbo-scope-${scope}`}
                  />
                  <span>{scope.replace('_', ' ')}</span>
                </label>
              ))}
            </fieldset>
            <label className="ferro-field">
              <span className="ferro-field__label">
                Accountant email (optional)
              </span>
              <input
                type="email"
                className="ferro-field__input"
                value={qb.accountantEmailQbo}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  patchQb({ accountantEmailQbo: e.target.value })
                }
                placeholder="accountant@example.com"
                data-testid="intake-step4-qbo-accountant-email"
              />
            </label>
          </div>
        ) : null}

        {/* QBD variant fields */}
        {qb.variant === 'qbd' ? (
          <div
            className="ferro-intake__conditional"
            data-testid="intake-step4-qbd-block"
          >
            <label className="ferro-field">
              <span className="ferro-field__label">Accountant name</span>
              <input
                type="text"
                className="ferro-field__input"
                value={qb.accountantName}
                required
                minLength={2}
                maxLength={120}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  patchQb({ accountantName: e.target.value })
                }
                aria-invalid={qbAccountantNameServerError ? true : undefined}
                aria-describedby={
                  qbAccountantNameServerError
                    ? fieldErrorId('qbd-accountant-name')
                    : undefined
                }
                data-testid="intake-step4-qbd-accountant-name"
              />
              {qbAccountantNameServerError ? (
                <span
                  id={fieldErrorId('qbd-accountant-name')}
                  role="alert"
                  className="ferro-form__error"
                >
                  {qbAccountantNameServerError}
                </span>
              ) : null}
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">Accountant email</span>
              <input
                type="email"
                className="ferro-field__input"
                value={qb.accountantEmail}
                required
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  patchQb({ accountantEmail: e.target.value })
                }
                placeholder="accountant@example.com"
                aria-invalid={
                  qbAccountantEmailQbdServerError
                    ? true
                    : undefined
                }
                aria-describedby={
                  qbAccountantEmailQbdServerError
                    ? fieldErrorId('qbd-accountant-email')
                    : undefined
                }
                data-testid="intake-step4-qbd-accountant-email"
              />
              {qbAccountantEmailQbdServerError ? (
                <span
                  id={fieldErrorId('qbd-accountant-email')}
                  role="alert"
                  className="ferro-form__error"
                >
                  {qbAccountantEmailQbdServerError}
                </span>
              ) : null}
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">Export cadence</span>
              <select
                className="ferro-field__input"
                value={qb.exportCadence}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  patchQb({
                    exportCadence: readCadence(e.target.value) || '',
                  })
                }
                aria-invalid={qbExportCadenceServerError ? true : undefined}
                data-testid="intake-step4-qbd-cadence"
              >
                <option value="">Select…</option>
                {EXPORT_CADENCE_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">
                Note for accountant (optional)
              </span>
              <textarea
                className="ferro-field__input"
                value={qb.comment}
                rows={2}
                maxLength={500}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  patchQb({ comment: e.target.value })
                }
                data-testid="intake-step4-qbd-comment"
              />
            </label>
          </div>
        ) : null}

        {/* None variant fields */}
        {qb.variant === 'none' ? (
          <div
            className="ferro-intake__conditional"
            data-testid="intake-step4-none-block"
          >
            <label className="ferro-field ferro-intake__checkbox">
              <input
                type="checkbox"
                checked={qb.confirmedNoAccountingSync}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  patchQb({ confirmedNoAccountingSync: e.target.checked })
                }
                aria-invalid={qbConfirmNoneServerError ? true : undefined}
                data-testid="intake-step4-qb-confirm-none"
              />
              <span className="ferro-field__label">
                I confirm no accounting sync is needed.
              </span>
            </label>
            {qbConfirmNoneServerError ? (
              <span role="alert" className="ferro-form__error">
                {qbConfirmNoneServerError}
              </span>
            ) : null}
          </div>
        ) : null}
      </section>

      {/* ── ACH ────────────────────────────────────────────── */}
      <section
        className="ferro-field"
        aria-labelledby="intake-step4-ach-heading"
      >
        <h2 id="intake-step4-ach-heading" className="ferro-field__label">
          ACH / bank transfers
        </h2>
        <p className="ferro-muted">
          Optional. Useful for larger clients (multi-dog boarding, corporate
          accounts). Turn on to collect the info we'll need during activation.
        </p>
        <label className="ferro-field ferro-intake__checkbox">
          <input
            type="checkbox"
            checked={ach.desired}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              patchAch({ desired: e.target.checked })
            }
            data-testid="intake-step4-ach-desired"
          />
          <span className="ferro-field__label">
            Yes, enable ACH payments for this tenant.
          </span>
        </label>

        {ach.desired ? (
          <div
            className="ferro-intake__conditional"
            data-testid="intake-step4-ach-block"
          >
            <label className="ferro-field">
              <span className="ferro-field__label">Bank name</span>
              <input
                type="text"
                className="ferro-field__input"
                value={ach.bankName}
                required
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  patchAch({ bankName: e.target.value })
                }
                aria-invalid={achBankError ? true : undefined}
                aria-describedby={
                  achBankError ? fieldErrorId('ach-bank-name') : undefined
                }
                data-testid="intake-step4-ach-bank-name"
              />
              {achBankError ? (
                <span
                  id={fieldErrorId('ach-bank-name')}
                  role="alert"
                  className="ferro-form__error"
                >
                  {achBankError}
                </span>
              ) : null}
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">Contact name</span>
              <input
                type="text"
                className="ferro-field__input"
                value={ach.contactName}
                required
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  patchAch({ contactName: e.target.value })
                }
                aria-invalid={achContactNameError ? true : undefined}
                aria-describedby={
                  achContactNameError
                    ? fieldErrorId('ach-contact-name')
                    : undefined
                }
                data-testid="intake-step4-ach-contact-name"
              />
              {achContactNameError ? (
                <span
                  id={fieldErrorId('ach-contact-name')}
                  role="alert"
                  className="ferro-form__error"
                >
                  {achContactNameError}
                </span>
              ) : null}
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">
                Contact email (email or phone required)
              </span>
              <input
                type="email"
                className="ferro-field__input"
                value={ach.contactEmail}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  patchAch({ contactEmail: e.target.value })
                }
                aria-invalid={
                  achEmailClientError || achEmailServerError ? true : undefined
                }
                aria-describedby={
                  achEmailClientError || achEmailServerError
                    ? fieldErrorId('ach-email')
                    : undefined
                }
                data-testid="intake-step4-ach-contact-email"
              />
              {achEmailClientError ? (
                <span
                  id={fieldErrorId('ach-email')}
                  role="alert"
                  className="ferro-form__error"
                >
                  {achEmailClientError}
                </span>
              ) : achEmailServerError ? (
                <span
                  id={fieldErrorId('ach-email')}
                  role="alert"
                  className="ferro-form__error"
                >
                  {achEmailServerError}
                </span>
              ) : null}
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">
                Contact phone (email or phone required)
              </span>
              <input
                type="tel"
                className="ferro-field__input"
                value={ach.contactPhone}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  patchAch({ contactPhone: e.target.value })
                }
                aria-invalid={
                  achPhoneClientError || achPhoneServerError ? true : undefined
                }
                aria-describedby={
                  achPhoneClientError || achPhoneServerError
                    ? fieldErrorId('ach-phone')
                    : undefined
                }
                data-testid="intake-step4-ach-contact-phone"
              />
              {achPhoneClientError ? (
                <span
                  id={fieldErrorId('ach-phone')}
                  role="alert"
                  className="ferro-form__error"
                >
                  {achPhoneClientError}
                </span>
              ) : achPhoneServerError ? (
                <span
                  id={fieldErrorId('ach-phone')}
                  role="alert"
                  className="ferro-form__error"
                >
                  {achPhoneServerError}
                </span>
              ) : null}
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">Note (optional)</span>
              <textarea
                className="ferro-field__input"
                value={ach.note}
                rows={2}
                maxLength={500}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  patchAch({ note: e.target.value })
                }
                data-testid="intake-step4-ach-note"
              />
            </label>
            {achDesiredGateMessage ? (
              <span
                role="alert"
                className="ferro-form__error"
                data-testid="intake-step4-ach-gate-error"
              >
                {achDesiredGateMessage}
              </span>
            ) : null}
            {achRootServerError ? (
              <span
                role="alert"
                className="ferro-form__error"
                data-testid="intake-step4-ach-root-servererror"
              >
                {achRootServerError}
              </span>
            ) : null}
          </div>
        ) : null}
      </section>

      {/* ── Payment channels ─────────────────────────────── */}
      <section
        className="ferro-field"
        aria-labelledby="intake-step4-channels-heading"
      >
        <h2 id="intake-step4-channels-heading" className="ferro-field__label">
          Payment channels
        </h2>
        <p className="ferro-muted">
          Which surfaces will take money? You can change these later.
        </p>
        <label className="ferro-field ferro-intake__checkbox">
          <input
            type="checkbox"
            checked={ch.online}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              patchChannels({ online: e.target.checked })
            }
            data-testid="intake-step4-channel-online"
          />
          <span className="ferro-field__label">Online (web + mobile)</span>
        </label>
        <label className="ferro-field ferro-intake__checkbox">
          <input
            type="checkbox"
            checked={ch.inPerson}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const nextInPerson = e.target.checked;
              patchChannels(
                nextInPerson
                  ? { inPerson: true }
                  : { inPerson: false, terminalModel: '' },
              );
            }}
            data-testid="intake-step4-channel-in-person"
          />
          <span className="ferro-field__label">In-person (Stripe Terminal)</span>
        </label>
        <label className="ferro-field ferro-intake__checkbox">
          <input
            type="checkbox"
            checked={ch.manualPhone}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              patchChannels({ manualPhone: e.target.checked })
            }
            data-testid="intake-step4-channel-manual-phone"
          />
          <span className="ferro-field__label">
            Manual phone (staff keys in cards)
          </span>
        </label>

        {ch.inPerson ? (
          <label
            className="ferro-field"
            data-testid="intake-step4-terminal-block"
          >
            <span className="ferro-field__label">Terminal model</span>
            <select
              className="ferro-field__input"
              value={ch.terminalModel}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                patchChannels({
                  terminalModel: readTerminal(e.target.value) || '',
                })
              }
              aria-invalid={channelsTerminalServerError ? true : undefined}
              data-testid="intake-step4-terminal-model"
            >
              <option value="">Select…</option>
              <option value="bbpos_wisepos_e">BBPOS WisePOS E</option>
              <option value="stripe_reader_s700">Stripe Reader S700</option>
              <option value="tbd">Decide later</option>
            </select>
            {channelsTerminalServerError ? (
              <span role="alert" className="ferro-form__error">
                {channelsTerminalServerError}
              </span>
            ) : null}
          </label>
        ) : null}

        {channelsRootServerError ? (
          <span
            role="alert"
            className="ferro-form__error"
            data-testid="intake-step4-channels-root-servererror"
          >
            {channelsRootServerError}
          </span>
        ) : null}
      </section>
    </form>
  );
}
